import isPermissionEnabled from "common/isPermissionEnabled"
import Breadcrumbs from "components/Common/Breadcrumb"
import BackButton from "components/Shared/BackButton"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { ShimmerThumbnail } from "react-shimmer-effects"
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Row,
  Table,
} from "reactstrap"
import { getPosBranchDetails, syncPosBranch } from "store/actions/posAction"
import { showAlert } from "store/reducers/alertReducer"

const BranchSyncDetails = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t(`Branch Sync Details `)

  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { loading, isLoading, posBranchDetails } = useSelector(
    state => state.POS
  )

  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")

  const { userRole, accountType } = useSelector(state => state.Login)

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      29,
      "branch_sync_1",
      accountType
    )

    if (!isPermissionList) {
      navigate("/dashboard")
    }
  }, [userRole])

  useEffect(() => {
    const fetchOldDetails = () => {
      dispatch(getPosBranchDetails(id))
    }
    fetchOldDetails()
  }, [])

  const copyToClipboard = data => {
    const dataString = JSON.stringify(data, null, 2)

    const textarea = document.createElement("textarea")
    textarea.value = dataString
    textarea.setAttribute("readonly", "")
    textarea.style.position = "absolute"
    textarea.style.left = "-9999px"
    document.body.appendChild(textarea)

    try {
      textarea.select()
      const successful = document.execCommand("copy")
      if (successful) {
        dispatch(
          showAlert({ text: t("Data copied to clipboard!"), type: "success" })
        )
      } else {
        dispatch(showAlert({ text: t("Failed to copy"), type: "failed" }))
      }
    } catch (err) {
      dispatch(showAlert({ text: t("Failed to copy"), type: "failed" }))
    } finally {
      document.body.removeChild(textarea)
    }
  }

  const handleBranchSync = (id, countryCode) => {
    const payload = {
      countryCode,
      id,
    }
    dispatch(syncPosBranch(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between">
                  <CardTitle className="d-flex align-items-center">
                    <BackButton /> {t("Branch Sync Details")}
                  </CardTitle>
                  <Button
                    color="primary"
                    disabled={isLoading}
                    onClick={() => {
                      handleBranchSync(
                        posBranchDetails._id,
                        posBranchDetails.country
                      )
                    }}
                  >
                    {t("Sync")}
                  </Button>
                </div>
                <p className="text-muted mb-4"></p>
                <div className="table-responsive">
                  {loading ? (
                    <ShimmerThumbnail height={650} rounded />
                  ) : Object.values(posBranchDetails).length === 0 ? (
                    <p className="text-center my-2">{t("No data found")}</p>
                  ) : (
                    <Table className="table-nowrap mb-0 capitalize_td">
                      <tbody>
                        <tr>
                          <th scope="row">{t("Id")}</th>
                          <td>{posBranchDetails._id}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Name")}</th>
                          <td>
                            {language === "sa"
                              ? posBranchDetails.nameAr
                              : posBranchDetails.name}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Branch Status")}</th>
                          <td>
                            {posBranchDetails.status === 1 ? (
                              <Badge className="bg-success">
                                {t("Active")}
                              </Badge>
                            ) : (
                              <Badge className="bg-danger">
                                {t("Deactive")}
                              </Badge>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Organization Id Status")}</th>
                          <td>
                            {posBranchDetails?.posSettings?.organizationId ? (
                              <Badge className="bg-success">
                                {t("Connected")}
                              </Badge>
                            ) : (
                              <Badge className="bg-danger">
                                {t("Not Connected")}
                              </Badge>
                            )}
                          </td>
                        </tr>
                        {posBranchDetails?.posSettings?.organizationId && (
                          <tr>
                            <th scope="row">{t("Organization Id")}</th>
                            <td>
                              {posBranchDetails?.posSettings?.organizationId}

                              <i
                                className="mdi mdi-content-copy cursor-pointer"
                                style={{
                                  fontSize: 18,
                                  marginLeft: 15,
                                }}
                                onClick={() =>
                                  copyToClipboard(
                                    posBranchDetails?.posSettings
                                      ?.organizationId
                                  )
                                }
                              ></i>
                            </td>
                          </tr>
                        )}
                        <tr>
                          <th scope="row">{t("Foodics Branch Id Status")}</th>
                          <td>
                            {posBranchDetails?.posSettings?.foodicsBranchId ? (
                              <Badge className="bg-success">
                                {t("Connected")}
                              </Badge>
                            ) : (
                              <Badge className="bg-danger">
                                {t("Not Connected")}
                              </Badge>
                            )}
                          </td>
                        </tr>
                        {posBranchDetails?.posSettings?.foodicsBranchId && (
                          <tr>
                            <th scope="row">{t("Foodics Branch Id")}</th>
                            <td>
                              {posBranchDetails?.posSettings?.foodicsBranchId}
                              <i
                                className="mdi mdi-content-copy cursor-pointer"
                                style={{
                                  fontSize: 18,
                                  marginLeft: 15,
                                }}
                                onClick={() =>
                                  copyToClipboard(
                                    posBranchDetails?.posSettings
                                      ?.foodicsBranchId
                                  )
                                }
                              ></i>
                            </td>
                          </tr>
                        )}
                        <tr>
                          <th scope="row">{t("Terminal Group Id Status")}</th>
                          <td>
                            {posBranchDetails?.posSettings?.terminalGroupId ? (
                              <Badge className="bg-success">
                                {t("Connected")}
                              </Badge>
                            ) : (
                              <Badge className="bg-danger">
                                {t("Not Connected")}
                              </Badge>
                            )}
                          </td>
                        </tr>
                        {posBranchDetails?.posSettings?.terminalGroupId && (
                          <tr>
                            <th scope="row">{t("Terminal Group Id")}</th>
                            <td>
                              {posBranchDetails?.posSettings?.terminalGroupId}
                              <i
                                className="mdi mdi-content-copy cursor-pointer"
                                style={{
                                  fontSize: 18,
                                  marginLeft: 15,
                                }}
                                onClick={() =>
                                  copyToClipboard(
                                    posBranchDetails?.posSettings
                                      ?.terminalGroupId
                                  )
                                }
                              ></i>
                            </td>
                          </tr>
                        )}
                        <tr>
                          <th scope="row">{t("Webhook URL Status")}</th>
                          <td>
                            {posBranchDetails?.posSettings?.webhookUrl ? (
                              <Badge className="bg-success">{t("Setup")}</Badge>
                            ) : (
                              <Badge className="bg-danger">
                                {t("Not Setup")}
                              </Badge>
                            )}
                          </td>
                        </tr>
                        {posBranchDetails?.posSettings?.webhookUrl && (
                          <tr>
                            <th scope="row">{t("Webhook URL")}</th>
                            <td>
                              {posBranchDetails?.posSettings?.webhookUrl}
                              <i
                                className="mdi mdi-content-copy cursor-pointer"
                                style={{
                                  fontSize: 18,
                                  marginLeft: 15,
                                }}
                                onClick={() =>
                                  copyToClipboard(
                                    posBranchDetails?.posSettings?.webhookUrl
                                  )
                                }
                              ></i>
                            </td>
                          </tr>
                        )}
                        <tr>
                          <th scope="row">{t("Payment Type Status")}</th>
                          <td>
                            {posBranchDetails?.posSettings?.paymentTypeId ? (
                              <Badge className="bg-success">
                                {t("Connected")}
                              </Badge>
                            ) : (
                              <Badge className="bg-danger">
                                {t("Not Connected")}
                              </Badge>
                            )}
                          </td>
                        </tr>
                        {posBranchDetails?.posSettings?.paymentTypeId && (
                          <tr>
                            <th scope="row">{t("Payment Type Id")}</th>
                            <td>
                              {posBranchDetails?.posSettings?.paymentTypeId}
                              <i
                                className="mdi mdi-content-copy cursor-pointer"
                                style={{
                                  fontSize: 18,
                                  marginLeft: 15,
                                }}
                                onClick={() =>
                                  copyToClipboard(
                                    posBranchDetails?.posSettings?.paymentTypeId
                                  )
                                }
                              ></i>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default BranchSyncDetails
